import { useTraduccion } from '@tallerya/i18n';
import { AvisoError, Boton, Chip, IconoMas, Menu, PanelLateral, type ElementoMenu } from '@tallerya/ui';
import { useState } from 'react';
import { useAccionDeFila } from '../../componentes/useAccionDeFila';
import { useCambiosTipos, useTipo, version, type Campo, type Tipo } from './datos';
import { NOMBRE_DEL_DATO, VentanaCampo, type EdicionCampo } from './VentanaCampo';

// Los campos de un tipo de activo (#117): lo que el formulario del activo pide,
// en este orden. Se agregan, se editan, se apagan y se reordenan. Nada se borra:
// un campo apagado conserva lo que los activos ya tenían.
//
// Era la ficha del tipo, con pantalla propia. Desde #154 los tipos se mantienen
// en Configuraciones › Catálogos, y sus campos salen en este panel por la
// derecha, encima del catálogo: se edita sin perder de vista la lista.
//
// **La ventana del campo va al lado del panel, no adentro.** Las dos son
// `<dialog>`, y en React el `cancel` y el `close` de una ventana dibujada dentro
// del panel suben hasta él: Escape, «Guardar» o «Cancelar» en el campo cerrarían
// también el panel.

interface Props {
  /** El tipo cuyos campos se muestran, o `null` con el panel cerrado. */
  tipoId: string | null;
  onCerrar: () => void;
}

export function PanelCampos({ tipoId, onCerrar }: Props) {
  const { t } = useTraduccion();
  const consulta = useTipo(tipoId ?? undefined);
  const ficha = consulta.data ?? null;
  const [edicionCampo, setEdicionCampo] = useState<EdicionCampo>(null);

  return (
    <>
      <PanelLateral
        abierto={tipoId !== null}
        onCerrar={onCerrar}
        titulo={ficha ? t('admin.catalogos.tipos.camposDe', { tipo: ficha.name }) : t('admin.tipos.campos.titulo')}
        info={t('admin.tipos.campos.texto')}
      >
        {/* Con los campos ya leídos, un fallo al volver a pedirlos tras una
            escritura no los borra de la vista: el aviso lo da la acción. */}
        {ficha ? (
          <Campos ficha={ficha} onEditar={setEdicionCampo} />
        ) : consulta.isError ? (
          <div className="marcas__nota">
            <p>{t('ui.listado.error.texto')}</p>
            <Boton variante="secundario" onClick={() => void consulta.refetch()}>
              {t('app.reintentar')}
            </Boton>
          </div>
        ) : (
          <p className="marcas__nota">{t('app.cargando')}</p>
        )}
      </PanelLateral>

      {ficha && <VentanaCampo edicion={edicionCampo} ficha={ficha} onCerrar={() => setEdicionCampo(null)} />}
    </>
  );
}

function Campos({ ficha, onEditar }: { ficha: Tipo; onEditar: (edicion: EdicionCampo) => void }) {
  const { t } = useTraduccion();
  const cambios = useCambiosTipos();
  const { hacer, problema, ocupado } = useAccionDeFila();

  const tipo = String(ficha.id);
  const versionTipo = version(ficha.version);
  const campos = ficha.fields;

  /** Sube o baja un campo un lugar. Se manda el orden de todos, como pide la API. */
  function mover(indice: number, hacia: -1 | 1) {
    const orden = campos.map((c) => String(c.id));
    const destino = indice + hacia;
    const a = orden[indice];
    const b = orden[destino];
    if (a === undefined || b === undefined) return;
    orden[indice] = b;
    orden[destino] = a;
    void hacer(cambios.reordenar(tipo, versionTipo, orden), t('admin.tipos.aviso.reordenado'));
  }

  function opciones(campo: Campo): ElementoMenu[] {
    const nombre = { nombre: campo.label };
    return [
      { etiqueta: t('admin.tipos.menu.editarCampo'), onSeleccionar: () => onEditar({ tipo: 'editar', campoId: String(campo.id) }) },
      campo.isActive
        ? {
            etiqueta: t('admin.tipos.menu.apagar'),
            onSeleccionar: () =>
              void hacer(
                cambios.cambiarEstadoCampo(tipo, String(campo.id), versionTipo, false),
                t('admin.tipos.aviso.campoApagado', nombre),
              ),
          }
        : {
            etiqueta: t('admin.tipos.menu.encender'),
            onSeleccionar: () =>
              void hacer(
                cambios.cambiarEstadoCampo(tipo, String(campo.id), versionTipo, true),
                t('admin.tipos.aviso.campoEncendido', nombre),
              ),
          },
    ];
  }

  return (
    <div className="catalogos__panel">
      {problema && <AvisoError aviso={{ titulo: t('admin.tipos.error.titulo'), texto: problema }} />}

      <p className="tipos__resumen">
        {ficha.requiresIdentifier
          ? t('admin.tipos.resumen.exige', { identificador: ficha.identifierLabel })
          : t('admin.tipos.resumen.opcional', { identificador: ficha.identifierLabel })}
      </p>

      <Boton icono={<IconoMas />} onClick={() => onEditar({ tipo: 'nuevo' })}>
        {t('admin.tipos.agregarCampo')}
      </Boton>

      {campos.length === 0 ? (
        <p className="marcas__nota">{t('admin.tipos.campos.vacio')}</p>
      ) : (
        <ol className="marcas__lista tipos__campos">
          {campos.map((campo, i) => (
            <li key={String(campo.id)} className="marcas__fila tipos__campo">
              <span className="tipos__mover">
                <Boton
                  variante="texto"
                  soloIcono
                  disabled={ocupado || i === 0}
                  aria-label={t('admin.tipos.subir', { nombre: campo.label })}
                  onClick={() => mover(i, -1)}
                >
                  ↑
                </Boton>
                <Boton
                  variante="texto"
                  soloIcono
                  disabled={ocupado || i === campos.length - 1}
                  aria-label={t('admin.tipos.bajar', { nombre: campo.label })}
                  onClick={() => mover(i, 1)}
                >
                  ↓
                </Boton>
              </span>
              <span className="marcas__elegir marcas__elegir--fijo">
                <span className="marcas__nombre">{campo.label}</span>
                <span className="marcas__detalle">
                  <Chip tono="neutro">{t(NOMBRE_DEL_DATO[campo.valueType])}</Chip>
                  {campo.valueType === 'list' && (
                    <span className="marcas__conteo">
                      {t('admin.tipos.conteoOpciones', { n: campo.allowedValues?.length ?? 0 })}
                    </span>
                  )}
                  {campo.isRequired && <Chip tono="marca">{t('admin.tipos.obligatorio')}</Chip>}
                  {!campo.isActive && <Chip>{t('admin.tipos.apagado')}</Chip>}
                </span>
              </span>
              <Menu
                elementos={opciones(campo)}
                disparador={({ alternar, atributos }) => (
                  <button
                    type="button"
                    className="ui-tabla__mas"
                    aria-label={t('ui.listado.accionesFila')}
                    onClick={alternar}
                    {...atributos}
                  >
                    •••
                  </button>
                )}
              />
            </li>
          ))}
        </ol>
      )}

    </div>
  );
}
